/**
 * PRD.250 — New Outstanding Today drawer.
 *
 * Opened from the Homepage "New Outstanding Today" KPI tile. Lists the unpaid
 * invoices CREATED today (the same today-scoped set the metric aggregates).
 *
 * Each row mirrors the Collections Today drawer for consistency: an Open Patient
 * action plus an Open Invoice action that opens the invoice in an in-place
 * drawer overlay. The footer offers two follow-ups: jump to the Outstanding
 * Balances page filtered to today, or to the full Total Outstanding list.
 */

import { type CSSProperties, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@helios/ui';
import { usePowerSyncQuery } from '../../adapters/powersync';
import {
  qHomeNewOutstandingTodayDetail,
  type HomeNewOutstandingTodayDetailRow,
} from '@helios/data-access';
import { InvoicesTab } from '../patient/consultation/InvoicesTab';
import {
  DrawerSummaryBanner,
  DrawerRowCard,
  DrawerFooterButton,
  DrawerEmptyState,
  PatientAvatar,
  RowCTAButton,
} from './shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';

export interface NewOutstandingTodayDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dayStartUtc: string;
  dayEndUtc: string;
  portalStyle?: CSSProperties;
}

export function NewOutstandingTodayDrawer({
  open,
  onOpenChange,
  dayStartUtc,
  dayEndUtc,
  portalStyle,
}: NewOutstandingTodayDrawerProps) {
  const navigate = useNavigate();
  const fmtCompact = useFormatMoney({ compact: true });
  const fmtCurrency = (amount: number) => fmtCompact(Math.round(amount));
  const { data: rows = [] } = usePowerSyncQuery<HomeNewOutstandingTodayDetailRow>(
    qHomeNewOutstandingTodayDetail(dayStartUtc, dayEndUtc),
  );

  // Invoice opened in-place (overlaying this drawer) when a row is clicked —
  // mirrors the Collections Today drawer for consistency.
  const [openInvoice, setOpenInvoice] = useState<{
    patientId: string;
    invoiceId: string;
    invoiceNo: string | null;
  } | null>(null);

  const total = useMemo(() => rows.reduce((sum, r) => sum + (r.grand_total ?? 0), 0), [rows]);

  return (
    <>
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="New Outstanding Today"
      description="Unpaid charges created today"
      width={520}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', gap: 'var(--sp-2, 8px)' }}>
          <div style={{ flex: 1 }}>
            <DrawerFooterButton
              label="View Outstanding Today"
              color="#d97706"
              onClick={() => {
                // PRD.251 — Outstanding Balances page filtered to today-created.
                navigate('/clinic/outstanding?created=today');
                onOpenChange(false);
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <DrawerFooterButton
              label="View Total Outstanding"
              color="#dc2626"
              onClick={() => {
                navigate('/clinic/outstanding');
                onOpenChange(false);
              }}
            />
          </div>
        </div>
      }
    >
      <DrawerSummaryBanner
        icon="clock"
        iconBg="#fffbeb"
        iconColor="#d97706"
        value={fmtCurrency(total)}
        description={`${rows.length} new unpaid invoice${rows.length !== 1 ? 's' : ''} today`}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3, 12px)',
          padding: 'var(--sp-4, 16px)',
        }}
      >
        {rows.length === 0 && <DrawerEmptyState message="No new unpaid charges today" />}

        {rows.map((row) => (
          <DrawerRowCard key={row.invoice_id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
              <PatientAvatar name={row.patient_name} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm, 13px)',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    color: 'var(--text, #111827)',
                  }}
                >
                  {row.patient_name ?? 'Unknown'}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs, 12px)',
                    color: 'var(--text-muted, #6b7280)',
                  }}
                >
                  {row.invoice_no ?? '—'}
                </div>
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-base, 14px)',
                  fontWeight: 'var(--font-weight-semi, 600)',
                  color: 'var(--text, #111827)',
                }}
              >
                {fmtCurrency(row.grand_total)}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 'var(--sp-2, 8px)' }}>
              <RowCTAButton
                label="Open Patient"
                variant="outline"
                onClick={() => {
                  if (!row.patient_id) return;
                  navigate(`/patient/${row.patient_id}/encounters`);
                  onOpenChange(false);
                }}
              />
              {row.invoice_id && row.patient_id && (
                <RowCTAButton
                  label="Open Invoice"
                  variant="filled"
                  color="#d97706"
                  onClick={() =>
                    setOpenInvoice({
                      patientId: row.patient_id!,
                      invoiceId: row.invoice_id!,
                      invoiceNo: row.invoice_no,
                    })
                  }
                />
              )}
            </div>
          </DrawerRowCard>
        ))}
      </div>
    </Drawer>

      {/* Invoice opened in place — overlays the Outstanding drawer. */}
      <Drawer
        open={!!openInvoice}
        onOpenChange={(o) => !o && setOpenInvoice(null)}
        title={openInvoice ? `Invoice ${openInvoice.invoiceNo ?? ''}`.trim() : 'Invoice'}
        width="min(1100px, 96vw)"
        portalStyle={portalStyle}
      >
        {openInvoice && (
          <div style={{ padding: 'var(--sp-4)' }}>
            <InvoicesTab
              key={openInvoice.invoiceId}
              patientId={openInvoice.patientId}
              initialInvoiceId={openInvoice.invoiceId}
              singlePane
              hideHeader
            />
          </div>
        )}
      </Drawer>
    </>
  );
}
